import { useCurrentUser } from "hooks/useCurrentUser";
import { useProcessedManifest } from "hooks/useProcessedManifest";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { billOfLadingDrawerSections } from "./billOfLadingDrawerSections";
import { DiscrepancyReviewDialog } from "./DiscrepancyReviewDialog";
import { ManifestReviewDialog } from "./ManifestReviewDialog";
import { parseReviewParam, REVIEW_PARAM_KEY } from "./reviewParam";
import { useCanReviewDocuments } from "./useCanReviewDocuments";

interface ReviewPermalinkProps {
  /** The manifest the `?review=` permalink resolves against (the route's manifest). */
  processedManifestId: string;
  /** Fired once after the first successful discrepancy row mutation in the dialog. */
  onDiscrepancyMutated?: () => void;
}

/**
 * URL-driven open surface for the document-review and discrepancies dialogs.
 * Reads `?review=`:
 *  - `review=document` — opens ManifestReviewDialog for the manifest's source document.
 *  - `review=discrepancies` — opens DiscrepancyReviewDialog for the manifest.
 * Closing clears the param. Mount once on the manifest route, alongside
 * CompareSourcesPermalink.
 */
export function ReviewPermalink({
  processedManifestId,
  onDiscrepancyMutated,
}: ReviewPermalinkProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isStaff } = useCurrentUser();
  const canReview = useCanReviewDocuments();
  const parsed = parseReviewParam(searchParams.get(REVIEW_PARAM_KEY));

  const handleClose = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(REVIEW_PARAM_KEY);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  if (!parsed) return null;

  // Discrepancy review stays staff-only, matching its in-drawer entry point —
  // the dialog resolves and writes discrepancies, which customers only read.
  if (parsed === "discrepancies") {
    if (!isStaff) return null;
    return (
      <DiscrepancyReviewPermalink
        processedManifestId={processedManifestId}
        onMutated={onDiscrepancyMutated}
        onClose={handleClose}
      />
    );
  }

  // Document review asks for the write, not the read its resolvers would also
  // allow. `canReadDocumentManifestReview` deliberately requires no permission
  // to save, so following a link without one would fill in the whole form and
  // refuse at Confirm — the failure the grid chip and the drawer's own entry
  // point both go out of their way to avoid.
  if (!canReview) return null;

  return (
    <DocumentReviewPermalink processedManifestId={processedManifestId} onClose={handleClose} />
  );
}

/**
 * Resolves the manifest's tracking number and opens DiscrepancyReviewDialog, so
 * the title names the manifest the way the staff Manifest Review quick-link does.
 * Until the manifest loads the dialog falls back to the id prefix.
 */
function DiscrepancyReviewPermalink({
  processedManifestId,
  onMutated,
  onClose,
}: {
  processedManifestId: string;
  onMutated?: () => void;
  onClose: () => void;
}) {
  const { processedManifest } = useProcessedManifest(processedManifestId);
  return (
    <DiscrepancyReviewDialog
      processedManifestIds={[processedManifestId]}
      manifestTrackingNumber={processedManifest?.manifestTrackingNumber ?? undefined}
      onMutated={onMutated}
      onClose={onClose}
    />
  );
}

/**
 * Resolves the manifest's source document and opens ManifestReviewDialog. If the
 * manifest has finished loading with no source document, or is a bill of lading
 * (which reviews through the drawer's own dialog), it calls onClose (defensive — callers only open this when the manifest
 * has a document). Exported so the staff Manifest Review queue can open the
 * document-review modal in place (it resolves the doc by processedManifestId; staff
 * are authorized cross-tenant, so no tenant arg is needed).
 */
export function DocumentReviewPermalink({
  processedManifestId,
  onClose,
  tenantId,
}: {
  processedManifestId: string;
  onClose: () => void;
  /** Explicit tenant for cross-tenant (staff) callers off a tenant-scoped route. */
  tenantId?: string;
}) {
  const { processedManifest, loading } = useProcessedManifest(processedManifestId, tenantId);
  const sourceDocumentId = processedManifest?.sourceDocument?.id;
  // A bill reviews through its own dialog in the drawer, not the manifest editor.
  const isBillOfLading = billOfLadingDrawerSections(processedManifest?.documentKind).isBillOfLading;

  useEffect(() => {
    if (!loading && processedManifest && (!sourceDocumentId || isBillOfLading)) onClose();
  }, [loading, processedManifest, sourceDocumentId, isBillOfLading, onClose]);

  if (!sourceDocumentId || isBillOfLading) return null;

  return (
    <ManifestReviewDialog
      onClose={onClose}
      documentIds={[sourceDocumentId]}
      wasteManifestId={processedManifest?.documentManifestId ?? undefined}
    />
  );
}
